import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  List,
  Loading,
  TopToolbar,
  useDataProvider,
  useGetList,
  useListContext,
  useLocaleState,
  useNotify,
  useRedirect,
  useRefresh,
  useTranslate,
} from 'react-admin';
import { Link, useParams } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import EditIcon from '@mui/icons-material/Edit';
import { makeStyles } from '@mui/styles';
import { useEffect, useMemo, useState } from 'react';

const useStyles = makeStyles({
  emptyRow: { display: 'flex', marginTop: '32px', justifyContent: 'center' },
});

const sortByPriority = (rows: any[]) => {
  return [...rows].sort((a: any, b: any) => {
    const pa = Number.isFinite(Number(a?.priority)) ? Number(a.priority) : Number.MAX_SAFE_INTEGER;
    const pb = Number.isFinite(Number(b?.priority)) ? Number(b.priority) : Number.MAX_SAFE_INTEGER;

    if (pa !== pb) {
      return pa - pb;
    }

    return Number(a?.id ?? Number.MAX_SAFE_INTEGER) - Number(b?.id ?? Number.MAX_SAFE_INTEGER);
  });
};

const moveItem = (rows: any[], from: number, to: number) => {
  const next = [...rows];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

const ReorderableBlockRow = ({
  record,
  index,
  rowCount,
  locale,
  saving,
  moveRow,
  redirect,
  translate,
}: {
  record: any;
  index: number;
  rowCount: number;
  locale: string;
  saving: boolean;
  moveRow: (from: number, to: number) => void;
  redirect: (path: string) => void;
  translate: (key: string, options?: any) => string;
}) => {
  const contentText = record?.content?.[locale] || record?.content?.en || '';

  return (
    <TableRow hover>
      <TableCell>
        <Stack direction="row" spacing={0.5}>
          <IconButton
            size="small"
            disabled={saving || index === 0}
            onClick={() => moveRow(index, index - 1)}
            aria-label={translate('resources.destinationpages.structure.moveUp', { _: 'Flytta upp' })}
          >
            <ArrowUpwardIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={saving || index === rowCount - 1}
            onClick={() => moveRow(index, index + 1)}
            aria-label={translate('resources.destinationpages.structure.moveDown', { _: 'Flytta ned' })}
          >
            <ArrowDownwardIcon fontSize="small" />
          </IconButton>
        </Stack>
      </TableCell>
      <TableCell>{index + 1}</TableCell>
      <TableCell>{record.blockType}</TableCell>
      <TableCell>{record.mediaHeight ?? '-'}</TableCell>
      <TableCell>{contentText}</TableCell>
      <TableCell align="right">
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => redirect(`/destinationpages/blocks/${record.id}`)}
        >
          {translate('ra.action.edit')}
        </Button>
      </TableCell>
    </TableRow>
  );
};

const ReorderableBlocksTable = () => {
  const { data, isLoading, isFetching } = useListContext();
  const [locale] = useLocaleState();
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const dataProvider = useDataProvider();

  const initialRows = useMemo(() => sortByPriority(data ?? []), [data]);
  const [rows, setRows] = useState<any[]>(initialRows);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(initialRows);
  }, [initialRows]);

  const hasPendingOrder = useMemo(() => {
    if (rows.length !== initialRows.length) {
      return true;
    }

    return rows.some((row, index) => row?.id !== initialRows[index]?.id);
  }, [initialRows, rows]);

  const moveRow = (from: number, to: number) => {
    if (from === to || to < 0 || to >= rows.length) {
      return;
    }

    setRows(prevRows => moveItem(prevRows, from, to));
  };

  const saveOrder = async () => {
    if (!hasPendingOrder || saving) {
      return;
    }

    setSaving(true);
    try {
      const updates = rows.map((row, index) => ({
        id: row.id,
        priority: index + 1,
      }));

      await Promise.all(
        updates.map(update =>
          dataProvider.update('destinationpages/blocks', {
            id: update.id,
            data: { priority: update.priority },
            previousData: rows.find(row => row.id === update.id),
          }),
        ),
      );

      notify(translate('resources.destinationpages.structure.reorderSaved', { _: 'Ordningen har sparats.' }), { type: 'info' });
      refresh();
    } catch (error) {
      notify(translate('resources.destinationpages.structure.reorderFailed', { _: 'Kunde inte spara ordningen.' }), { type: 'warning' });
    } finally {
      setSaving(false);
    }
  };

  if (isLoading) {
    return <Loading />;
  }

  if (!rows.length) {
    return null;
  }

  return (
    <TableContainer component={Paper} variant="outlined">
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 1.5, borderBottom: '1px solid #eceff3' }}>
        <Typography variant="body2" color="text.secondary">
          {translate('resources.destinationpages.structure.reorderHint', {
            _: 'Anvand upp- och nedpilarna for att andra ordning. Klicka pa Spara ordning nar du ar klar.',
          })}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={saveOrder}
          disabled={!hasPendingOrder || saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {translate('resources.destinationpages.structure.saveOrder', { _: 'Spara ordning' })}
        </Button>
      </Stack>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell width={92}></TableCell>
            <TableCell>{translate('resources.destinationpages.fields.priority')}</TableCell>
            <TableCell>{translate('resources.destinationpages.fields.blockType')}</TableCell>
            <TableCell>{translate('resources.destinationpages.fields.mediaHeight')}</TableCell>
            <TableCell>{translate('resources.destinationpages.fields.content')}</TableCell>
            <TableCell align="right"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((record, index) => (
            <ReorderableBlockRow
              key={record.id}
              record={record}
              index={index}
              rowCount={rows.length}
              locale={locale}
              saving={saving}
              moveRow={moveRow}
              redirect={redirect}
              translate={translate}
            />
          ))}
        </TableBody>
      </Table>

      {isFetching ? (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 1.5, py: 1 }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">
            {translate('ra.page.loading')}
          </Typography>
        </Stack>
      ) : null}
    </TableContainer>
  );
};

const CustomCreateButton = (props: any) => {
  const { pageId } = useParams();
  const translate = useTranslate();
  return (
    <Button
      {...props}
      component={Link}
      to={`/destinationpages/${pageId}/blocks/create`}
      startIcon={<AddIcon />}
    >
      {translate('resources.destinationpages.create')}
    </Button>
  );
};

const CustomActions = () => {
  const { pageId } = useParams();
  const translate = useTranslate();

  return (
    <TopToolbar>
      <Button
        component={Link}
        to={`/destinationpages/${pageId}`}
        startIcon={<ArrowBackIcon />}
      >
        {translate('resources.destinationpages.structure.backToPage')}
      </Button>
      <CustomCreateButton />
    </TopToolbar>
  );
};

export const DestinationPageBlockList = () => {
  const { pageId } = useParams();
  const classes = useStyles();
  const list = useGetList('destinationpages/blocks', {
    filter: { pageId },
    sort: { field: 'priority', order: 'ASC' },
    pagination: { page: 1, perPage: 100 },
  });

  return (
    <>
      <List
        resource="destinationpages/blocks"
        filter={{ pageId }}
        sort={{ field: 'priority', order: 'ASC' }}
        perPage={100}
        pagination={false}
        actions={<CustomActions />}
      >
        <ReorderableBlocksTable />
      </List>
      {list.isLoading ? null : <EmptyBlocksState classes={classes} hasData={(list.data ?? []).length > 0} />}
    </>
  );
};

const EmptyBlocksState = ({ classes, hasData }: { classes: any; hasData: boolean }) => {
  if (hasData) {
    return null;
  }

  return (
    <div className={classes.emptyRow}>
      <CustomCreateButton variant="contained" size="small" />
    </div>
  );
};
